import { create } from 'zustand';

/**
 * Toast (transient notification) store.
 *
 * The app previously had NO feedback layer at all: destroy, hibernate, resume,
 * start-phase, stop-lab and extend-lab all resolved silently, so a failed
 * mutation was indistinguishable from a successful one — the operator just saw
 * the spinner stop. This is a dependency-free replacement for
 * `@mantine/notifications` (adding a package would touch files outside src/).
 *
 * `toast.*` is callable from anywhere, including non-React code such as the
 * TanStack Query `MutationCache` in lib/queryClient.ts.
 */
export type ToastTone = 'success' | 'error' | 'info' | 'warn';

export interface Toast {
  id: string;
  tone: ToastTone;
  title: string;
  message?: string;
  /** ms before auto-dismiss. 0 keeps it until dismissed. */
  duration: number;
}

interface ToastState {
  toasts: ReadonlyArray<Toast>;
  push: (t: Omit<Toast, 'id' | 'duration'> & { duration?: number }) => string;
  dismiss: (id: string) => void;
  clear: () => void;
}

/** Errors stay up longer than confirmations — the operator must be able to read them. */
const DEFAULT_DURATION: Record<ToastTone, number> = {
  success: 4000,
  info: 4000,
  warn: 6000,
  error: 8000,
};

let seq = 0;

export const useToasts = create<ToastState>((set) => ({
  toasts: [],
  push: (t) => {
    const id = `t${++seq}`;
    const toast: Toast = {
      id,
      tone: t.tone,
      title: t.title,
      ...(t.message !== undefined ? { message: t.message } : {}),
      duration: t.duration ?? DEFAULT_DURATION[t.tone],
    };
    // Cap the stack so a retry storm can't bury the screen.
    set((s) => ({ toasts: [...s.toasts, toast].slice(-4) }));
    return id;
  },
  dismiss: (id) => set((s) => ({ toasts: s.toasts.filter((x) => x.id !== id) })),
  clear: () => set({ toasts: [] }),
}));

const emit =
  (tone: ToastTone) =>
  (title: string, message?: string, duration?: number): string =>
    useToasts.getState().push({
      tone,
      title,
      ...(message !== undefined ? { message } : {}),
      ...(duration !== undefined ? { duration } : {}),
    });

export const toast = {
  success: emit('success'),
  error: emit('error'),
  info: emit('info'),
  warn: emit('warn'),
  dismiss: (id: string) => useToasts.getState().dismiss(id),
};
